import type { HTMLAttributeAnchorTarget, MouseEvent } from "react";
// eslint-disable-next-line no-restricted-imports
import { Menu as MenuBase } from "@base-ui/react/menu";
import { useHref, useLinkClickHandler, type To } from "react-router-dom";
import { usePersistedLocationState } from "@hooks/usePersistedLocationState";
import {
    MenuItemContent,
    menuItemClassName,
    renderRowWithTooltip,
} from "./MenuItemContent";
import type { MenuItemProps, MenuLinkItemProps } from "./Menu.types";

/**
 * A row that runs an action. Closes the menu on click unless
 * `closeOnClick` is false. An enabled row can show a `tooltip`; a disabled
 * row stays focusable and shows its `disabledReason` instead.
 */
export const MenuItem = ({
    children,
    leading,
    description,
    rightText,
    onClick,
    destructive = false,
    disabled = false,
    disabledReason,
    tooltip,
    closeOnClick = true,
    className,
    ref,
    "data-forced-state": forcedState,
}: MenuItemProps) => (
    <MenuBase.Item
        ref={ref}
        disabled={disabled}
        closeOnClick={closeOnClick}
        onClick={onClick}
        className={menuItemClassName({
            hasDescription: description != null,
            isDestructive: destructive,
            className,
        })}
        data-forced-state={forcedState}
        render={renderRowWithTooltip({
            isDisabled: disabled,
            disabledReason,
            tooltip,
        })}
    >
        <MenuItemContent
            leading={leading}
            description={description}
            rightText={rightText}
        >
            {children}
        </MenuItemContent>
    </MenuBase.Item>
);

type MenuLinkRowProps = Omit<MenuLinkItemProps, "to" | "href" | "target"> & {
    href: string;
    target?: HTMLAttributeAnchorTarget;
};

const MenuLinkRow = ({
    children,
    leading,
    description,
    rightText,
    href,
    target,
    onClick,
    closeOnClick = true,
    className,
    ref,
    "data-forced-state": forcedState,
}: MenuLinkRowProps) => (
    <MenuBase.LinkItem
        ref={ref}
        href={href}
        target={target}
        rel={
            target === undefined || target === "_self"
                ? undefined
                : "noopener noreferrer"
        }
        onClick={onClick}
        closeOnClick={closeOnClick}
        className={menuItemClassName({
            hasDescription: description != null,
            isDestructive: false,
            className,
        })}
        data-forced-state={forcedState}
    >
        <MenuItemContent
            leading={leading}
            description={description}
            rightText={rightText}
        >
            {children}
        </MenuItemContent>
    </MenuBase.LinkItem>
);

/** An in-app link: navigates through the router and keeps the scope's location state. */
const MenuRouterLinkItem = ({
    to,
    onClick,
    ...rowProps
}: Extract<MenuLinkItemProps, { to: To }>) => {
    const state = usePersistedLocationState();
    const href = useHref(to);
    const navigate = useLinkClickHandler(to, { state });

    return (
        <MenuLinkRow
            {...rowProps}
            href={href}
            onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                onClick?.(event);
                if (!event.defaultPrevented) {
                    navigate(event);
                }
            }}
        />
    );
};

/**
 * A row that navigates: `to` for an in-app route, `href` (and `target`) for
 * an external URL. Rendered as an `<a>`, so it opens in a new tab like any
 * link, and closes the menu on click.
 */
export const MenuLinkItem = (props: MenuLinkItemProps) =>
    props.to === undefined ? (
        <MenuLinkRow {...props} />
    ) : (
        <MenuRouterLinkItem {...props} to={props.to} />
    );
